import { Component, OnInit, Inject, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { AuthService } from '../../../services/auth.service';
import { BackupService } from '../../../services/backup.service';
import { JobTitle, NewJobTitle } from '../../../interfaces/job-title.interface';
import { InsuranceCompany } from '../../../interfaces/insurance-company.interface';
import { User } from '../../../interfaces/auth.interface';
import { AdminNavTabsComponent } from '../admin-nav-tabs/admin-nav-tabs.component';
import { AdminSettingsComponent } from '../admin-settings/admin-settings.component';
import { JobTitleDialog } from '../admin-settings/job-title-dialog/job-title-dialog.component';
import { InsuranceCompanyDialog } from '../admin-settings/insurance-company-dialog/insurance-company-dialog.component';
import { ColleagueTableComponent } from '../admin-colleagues/colleague-table/colleague-table.component';
import { AddColleagueDialog } from '../admin-colleagues/add-colleague-dialog/add-colleague-dialog';
import { ConfirmDialogComponent, AddNewButton } from '../../../shared/components';
import { ExtendedLogsWrapperComponent } from '../extended-logs/extended-logs-wrapper/extended-logs-wrapper.component';
import { AdminProductsComponent } from '../admin-products/admin-products.component';
import { AdminMonitoringComponent } from '../admin-monitoring/admin-monitoring.component';
import { RestoreProgressDialogComponent } from '../admin-settings/restore-progress-dialog';



@Component({
  selector: 'app-admin-wrapper',
  standalone: true,
  imports: [FormsModule, MatIconModule, MatSnackBarModule, MatDialogModule, MatButtonModule, MatCardModule, AdminNavTabsComponent, AdminSettingsComponent, ColleagueTableComponent, ExtendedLogsWrapperComponent, AdminProductsComponent, AdminMonitoringComponent, AddNewButton],
  templateUrl: './admin-wrapper.component.html',
  styleUrls: ['./admin-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminWrapperComponent implements OnInit {
  activeTab: string = 'colleagues';
  
  // Data loaded from database
  colleagues: User[] = [];
  jobTitles: JobTitle[] = [];
  insuranceCompanies: InsuranceCompany[] = [];
  lastManualBackupTime: string | null = null;

  constructor(
    private authService: AuthService,
    private backupService: BackupService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  private showNotification(message: string, type: 'success' | 'error' | 'info' = 'info') {
    const panelClass = type === 'success' ? 'success-snackbar' : 
                      type === 'error' ? 'error-snackbar' : 
                      'info-snackbar';
    
    this.snackBar.open(message, 'Bezárás', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: [panelClass]
    });
  }

  ngOnInit() {
    // Check if user is authenticated
    if (!this.authService.isAuthenticated()) {
      console.log('Nincs érvényes bejelentkezés, átirányítás a login oldalra');
      return;
    }
    
    this.loadUsers();
    this.loadJobTitles();
    this.loadInsuranceCompanies();
    this.loadLastManualBackupTime();
  }

  private loadUsers() {
    this.authService.getAllUsers().subscribe({
      next: (users) => {
        this.colleagues = users;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Hiba a felhasználók betöltése során:', error);
      }
    });
  }

  private loadJobTitles() {
    this.authService.getAllJobTitles().subscribe({
      next: (jobTitles) => {
        this.jobTitles = jobTitles;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Hiba a munkakörök betöltése során:', error);
      }
    });
  }

  private loadInsuranceCompanies() {
    this.authService.getAllInsuranceCompanies().subscribe({
      next: (insuranceCompanies) => {
        this.insuranceCompanies = insuranceCompanies;

        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Hiba a biztosítók betöltése során:', error);
        // Fallback mock adatok hiba esetén
        this.insuranceCompanies = [
          { id: 1, companyname: 'Allianz Hungária Zrt.' },
          { id: 2, companyname: 'Generali Biztosító Zrt.' },
          { id: 3, companyname: 'K&H Biztosító Zrt.' }
        ];

        this.cdr.markForCheck();
      }
    });
  }

  private loadLastManualBackupTime() {
    this.backupService.getLatestManualBackup().subscribe({
      next: (response) => {
        if (response.success && response.latest_backup) {
          const date = new Date(response.latest_backup.timestamp);
          this.lastManualBackupTime = this.formatDate(date);
        } else {
          this.lastManualBackupTime = null;
        }
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Hiba az utolsó manuális backup időpont betöltése során:', error);
        this.lastManualBackupTime = null;
        this.cdr.markForCheck();
      }
    });
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = date.toLocaleDateString('hu-HU', { month: 'long' });
    const day = date.getDate();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${year}. ${month} ${day}. ${hours}:${minutes}`;
  }

  // Tab navigation
  setActiveTab(tab: string) {
    this.activeTab = tab;
    this.cdr.markForCheck();
  }

  // Colleague management
  openColleagueForm() {
    const dialogRef = this.dialog.open(AddColleagueDialog, {
      width: '600px',
      data: {
        jobTitles: this.jobTitleNames,
        jobTitleObjects: this.jobTitles,
        editing: false
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success && result.shouldReload) {
        this.loadUsers();
      }
    });
  }

  editColleague(colleague: User) {
    const dialogRef = this.dialog.open(AddColleagueDialog, {
      width: '600px',
      data: {
        jobTitles: this.jobTitleNames,
        jobTitleObjects: this.jobTitles,
        editing: true,
        colleague: colleague
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success && result.shouldReload) {
        this.loadUsers();
      }
    });
  }

  deleteColleague(id: number) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '300px',
      data: {
        message: 'Biztosan törölni szeretné ezt a felhasználót?'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.authService.deleteUser(id).subscribe({
          next: (response) => {
            if (response.success) {
              console.log('Felhasználó sikeresen törölve:', response);
              this.loadUsers();
              setTimeout(() => {
                this.showNotification(response.message || 'Felhasználó sikeresen törölve', 'success');
              }, 100);
            } else {
              this.showNotification(response.message || 'Hiba történt a felhasználó törlése során', 'error');
            }
          },
          error: (error) => {
            console.error('Hiba a felhasználó törlése során:', error);
            
            // Check for foreign key constraint error
            if (error.error?.detail === 'FOREIGN_KEY_CONSTRAINT' || error.status === 409) {
              this.showNotification('A törlést a foreign key constraint (idegen kulcs megszorítás) akadályozza, tedd inaktívvá a felhasználót törlés helyett.', 'error');
            } else {
              this.showNotification(error.error?.detail || 'Hiba történt a felhasználó törlése során', 'error');
            }
          }
        });
      }
    });
  }

  // Analytics - placeholder methods
  getColleaguesByJobTitle(jobTitle: string) {
    return this.colleagues.filter(c => c.jobNames?.includes(jobTitle) && c.is_active);
  }

  get jobTitleNames() {
    return this.jobTitles.filter(jt => jt.is_active).map(jt => jt.title_name_hu);
  }

  // Job title management methods
  openJobTitleForm() {
    const dialogRef = this.dialog.open(JobTitleDialog, {
      width: '600px',
      data: {
        editingJobTitle: null,
        formData: {
          title_name_hu: '',
          title_name_en: '',
          role_key: '',
          is_active: true
        },
        isSubmitting: false
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.action === 'save') {
        this.submitJobTitle(result.data);
      }
    });
  }

  editJobTitle(jobTitle: JobTitle) {
    const dialogRef = this.dialog.open(JobTitleDialog, {
      width: '600px',
      data: {
        editingJobTitle: { ...jobTitle },
        formData: {
          title_name_hu: jobTitle.title_name_hu,
          title_name_en: jobTitle.title_name_en,
          role_key: jobTitle.role_key,
          is_active: jobTitle.is_active
        },
        isSubmitting: false
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.action === 'save') {
        this.submitJobTitle(result.data, jobTitle.id);
      } else if (result && result.action === 'delete') {
        this.deleteJobTitle(jobTitle.id);
      }
    });
  }

  submitJobTitle(formData: NewJobTitle, editingJobTitleId?: number) {
    // Validation
    if (!formData.title_name_hu.trim() || !formData.title_name_en.trim() || !formData.role_key.trim()) {
      this.showNotification('Kérjük, töltse ki minden mezőt!', 'error');
      return;
    }

    if (editingJobTitleId) {
      // Update existing job title
      this.authService.updateJobTitle(editingJobTitleId, formData).subscribe({
        next: (response: {success: boolean, message?: string}) => {
          if (response.success) {
            this.showNotification(response.message || 'Munkakör sikeresen frissítve!', 'success');
            this.loadJobTitles();
          } else {
            this.showNotification(response.message || 'Hiba történt a munkakör frissítése során', 'error');
          }
        },
        error: (error: any) => {
          console.error('Hiba a munkakör frissítése során:', error);
          this.showNotification(error.error?.detail || 'Hiba történt a munkakör frissítése során', 'error');
        }
      });
    } else {
      // Create new job title
      this.authService.createJobTitle(formData).subscribe({
        next: (response: {success: boolean, message?: string}) => {
          if (response.success) {
            this.showNotification(response.message || 'Munkakör sikeresen hozzáadva!', 'success');
            this.loadJobTitles();
          } else {
            this.showNotification(response.message || 'Hiba történt a munkakör létrehozása során', 'error');
          }
        },
        error: (error: any) => {
          console.error('Hiba a munkakör létrehozása során:', error);
          this.showNotification(error.error?.detail || 'Hiba történt a munkakör létrehozása során', 'error');
        }
      });
    }
  }

  deleteJobTitle(jobTitleId: number) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '300px',
      data: {
        message: 'Biztosan törölni szeretné ezt a munkakört?'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.authService.deleteJobTitle(jobTitleId).subscribe({
          next: (response) => {
            if (response.success) {
              console.log('Munkakör sikeresen törölve:', response);
              this.showNotification(response.message || 'Munkakör sikeresen törölve!', 'success');
              this.loadJobTitles();
            } else {
              this.showNotification(response.message || 'Hiba történt a munkakör törlése során', 'error');
            }
          },
          error: (error) => {
            console.error('Hiba a munkakör törlése során:', error);
            this.showNotification(error.error?.detail || 'Hiba történt a munkakör törlése során', 'error');
          }
        });
      }
    });
  }

  // Insurer management methods
  openInsuranceCompanyForm() {
    const dialogRef = this.dialog.open(InsuranceCompanyDialog, {
      width: '500px',
      data: {
        editingInsuranceCompany: null,
        formData: {
          companyname: ''
        },
        isSubmitting: false
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.action === 'save') {
        this.authService.createInsuranceCompany(result.data).subscribe({
          next: (response) => {
            if (response.success) {
              console.log('Biztosító sikeresen létrehozva:', response);
              this.showNotification(response.message || 'Új biztosító sikeresen létrehozva!', 'success');
              this.loadInsuranceCompanies();
            } else {
              this.showNotification(response.message || 'Hiba történt a biztosító létrehozása során', 'error');
            }
          },
          error: (error) => {
            console.error('Hiba a biztosító létrehozása során:', error);
            this.showNotification(error.error?.detail || 'Hiba történt a biztosító létrehozása során', 'error');
          }
        });
      }
    });
  }

  editInsuranceCompany(insuranceCompany: InsuranceCompany) {
    const dialogRef = this.dialog.open(InsuranceCompanyDialog, {
      width: '500px',
      data: {
        editingInsuranceCompany: insuranceCompany,
        formData: {
          companyname: insuranceCompany.companyname
        },
        isSubmitting: false
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.action === 'save') {
        this.authService.updateInsuranceCompany(insuranceCompany.id, result.data).subscribe({
          next: (response) => {
            if (response.success) {
              console.log('Biztosító sikeresen frissítve:', response);
              this.showNotification(response.message || 'Biztosító sikeresen frissítve!', 'success');
              this.loadInsuranceCompanies();
            } else {
              this.showNotification(response.message || 'Hiba történt a biztosító frissítése során', 'error');
            }
          },
          error: (error) => {
            console.error('Hiba a biztosító frissítése során:', error);
            this.showNotification(error.error?.detail || 'Hiba történt a biztosító frissítése során', 'error');
          }
        });
      } else if (result && result.action === 'delete') {
        this.deleteInsuranceCompany(insuranceCompany);
      }
    });
  }

  deleteInsuranceCompany(insuranceCompany: InsuranceCompany) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '300px',
      data: {
        message: `Biztosan törölni szeretné a(z) "${insuranceCompany.companyname}" biztosítót?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.authService.deleteInsuranceCompany(insuranceCompany.id).subscribe({
          next: (response) => {
            if (response.success) {
              console.log('Biztosító sikeresen törölve:', response);
              this.showNotification(response.message || 'Biztosító sikeresen törölve!', 'success');
              this.loadInsuranceCompanies();
            } else {
              this.showNotification(response.message || 'Hiba történt a biztosító törlése során', 'error');
            }
          },
          error: (error) => {
            console.error('Hiba a biztosító törlése során:', error);
            this.showNotification(error.error?.detail || 'Hiba történt a biztosító törlése során', 'error');
          }
        });
      }
    });
  }

  // Backup management methods
  onCreateBackup(): void {
    this.showNotification('Biztonsági mentés készítése...', 'info');
    
    this.backupService.createBackup().subscribe({
      next: (response) => {
        console.log('Backup response:', response);
        if (response.success) {
          this.showNotification(
            `Biztonsági mentés sikeresen elkészítve! Fájl: ${response.filename} (${response.file_size_mb} MB)`,
            'success'
          );
          if (response.timestamp) {
            const date = new Date(response.timestamp);
            const formattedDate = this.formatDate(date);
            console.log('Új backup időpont:', formattedDate);
            this.lastManualBackupTime = formattedDate;
            this.cdr.detectChanges();
          }
          this.loadLastManualBackupTime();
          
          // Automatikus letöltés indítása
          this.downloadBackupFile(response.backup_id, response.filename);
        } else {
          this.showNotification('Hiba történt a biztonsági mentés során', 'error');
        }
      },
      error: (error) => {
        console.error('Backup hiba:', error);
        this.showNotification(
          error.error?.detail || 'Hiba történt a biztonsági mentés során',
          'error'
        );
      }
    });
  }

  onRestoreBackup(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.sql,.dump,.backup';
    input.style.display = 'none';

    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        this.showNotification('Backup fájl feltöltése...', 'info');
        
        this.backupService.uploadBackup(file, 'Visszaállítás').subscribe({
          next: (uploadResponse) => {
            if (uploadResponse.status === 'complete' && uploadResponse.body?.success) {
              const backupId = uploadResponse.body.backup_id;
              
              // Progress dialógus megnyitása SSE streammel
              const dialogRef = this.dialog.open(RestoreProgressDialogComponent, {
                width: '500px',
                data: { backupId },
                disableClose: true
              });

              dialogRef.afterClosed().subscribe(result => {
                if (result?.success) {
                  window.location.reload();
                }
              });
            }
          },
          error: (error) => {
            console.error('Upload hiba:', error);
            this.showNotification('Hiba történt a fájl feltöltése során', 'error');
          }
        });
      }
    };

    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  }

  private downloadBackupFile(backupId: string, filename: string): void {
    this.showNotification('Backup fájl letöltése...', 'info');
    
    this.backupService.downloadBackup(backupId).subscribe({
      next: (blob: Blob) => {
        // Blob URL létrehozása
        const url = window.URL.createObjectURL(blob);
        
        // Letöltés indítása
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        
        // Cleanup
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        this.showNotification(`Backup fájl sikeresen letöltve: ${filename}`, 'success');
      },
      error: (error) => {
        console.error('Download hiba:', error);
        this.showNotification(
          error.error?.detail || 'Hiba történt a fájl letöltése során',
          'error'
        );
      }
    });
  }


}
